import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const DEBOUNCE_MS = 800;
const SAFETY_FLUSH_MS = 30000;
const backupKey = (courseId) => `ci-builder-backup-${courseId}`;

const readBackup = (courseId) => {
  try { return JSON.parse(localStorage.getItem(backupKey(courseId)) || '{}'); } catch { return {}; }
};

/**
 * Per-element save queue: merges rapid edits, never drops an element's changes,
 * keeps a browser backup until the server confirms, and can be flushed before publish/leave.
 */
export const useElementSaveQueue = (api, courseId) => {
  const pending = useRef({});      // elementId -> { moduleId, updates, ts }
  const timers = useRef({});
  const inflight = useRef({});     // elementId -> Promise
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [recovered, setRecovered] = useState(null);

  const writeBackup = useCallback(() => {
    if (!courseId) return;
    const keys = Object.keys(pending.current);
    if (keys.length) localStorage.setItem(backupKey(courseId), JSON.stringify(pending.current));
    else localStorage.removeItem(backupKey(courseId));
  }, [courseId]);

  useEffect(() => {
    if (!courseId) return;
    const b = readBackup(courseId);
    if (Object.keys(b).length) setRecovered(b);
  }, [courseId]);

  const flush = useCallback(async (elementId) => {
    clearTimeout(timers.current[elementId]);
    delete timers.current[elementId];
    if (inflight.current[elementId]) await inflight.current[elementId].catch(() => {});
    const item = pending.current[elementId];
    if (!item) return true;
    delete pending.current[elementId];
    setStatus('saving');
    const req = api.put(`/builder/modules/${item.moduleId}/elements/${elementId}`, item.updates);
    inflight.current[elementId] = req;
    try {
      await req;
      writeBackup();
      if (!Object.keys(pending.current).length) {
        setStatus('saved');
        setTimeout(() => setStatus(s => (s === 'saved' ? 'idle' : s)), 2000);
      }
      return true;
    } catch (e) {
      // Put the failed changes back underneath any newer edits so nothing is lost
      const newer = pending.current[elementId];
      pending.current[elementId] = { ...item, updates: { ...item.updates, ...(newer?.updates || {}) } };
      writeBackup();
      setStatus('error');
      const detail = e.response?.data?.detail;
      toast.error(`Changes NOT saved: ${typeof detail === 'string' ? detail : 'network or server error'}. Your edits are kept in this browser — click Retry.`, {
        id: 'builder-save-error',
        duration: 10000,
        action: { label: 'Retry', onClick: () => flush(elementId) },
      });
      return false;
    } finally {
      if (inflight.current[elementId] === req) delete inflight.current[elementId];
    }
  }, [api, writeBackup]);

  const queue = useCallback((moduleId, elementId, updates) => {
    const prev = pending.current[elementId];
    pending.current[elementId] = { moduleId, updates: { ...(prev?.updates || {}), ...updates }, ts: Date.now() };
    writeBackup();
    setStatus(s => (s === 'error' ? 'error' : 'pending'));
    clearTimeout(timers.current[elementId]);
    timers.current[elementId] = setTimeout(() => flush(elementId), DEBOUNCE_MS);
  }, [flush, writeBackup]);

  const flushAll = useCallback(async () => {
    const ids = [...new Set([...Object.keys(pending.current), ...Object.keys(inflight.current)])];
    const results = await Promise.all(ids.map(id => flush(id)));
    return results.every(Boolean);
  }, [flush]);

  const hasUnsaved = () => Object.keys(pending.current).length > 0 || Object.keys(inflight.current).length > 0;

  const discardRecovered = () => { localStorage.removeItem(backupKey(courseId)); setRecovered(null); };

  const restoreRecovered = async () => {
    Object.entries(recovered || {}).forEach(([id, item]) => { pending.current[id] = item; });
    setRecovered(null);
    return flushAll();
  };

  // Safety save every 30s, and warn before closing with unsaved work
  useEffect(() => {
    const iv = setInterval(() => { if (Object.keys(pending.current).length) flushAll(); }, SAFETY_FLUSH_MS);
    const onBeforeUnload = (e) => {
      if (!hasUnsaved()) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    // Flush the moment the tab is hidden, closed or backgrounded (mobile) — backup stays in localStorage until saved
    const onHide = () => { if (document.visibilityState === 'hidden') flushAll(); };
    const onPageHide = () => flushAll();
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(iv);
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [flushAll]);

  return { queue, flushAll, status, hasUnsaved, recovered, restoreRecovered, discardRecovered };
};
